// Export: the prompt book (or a department- and layer-filtered one) and cue sheets, rendered by
// the server from one saved version of the book. This device's unsent changes are sent first; if
// they can't be, the person chooses explicitly whether to export the saved version without them.

import { useEffect, useState } from "preact/hooks";
import { api, post } from "../account.ts";
import { ix, notify, pending, proposals, show } from "../state.ts";
import { kick, resync } from "../sync.ts";
import { Group, Sheet, SwitchRow } from "./kit.tsx";

interface Job {
  id: string;
  revision: number;
  state: "queued" | "running" | "done" | "failed";
  error: string | null;
  files: { name: string; bytes: number }[];
  warnings: string[];
  metrics: { wallMs: number } | null;
  createdAt: number;
}

const describe = (f: string) => (f.endsWith(".csv") ? "Cue sheet, spreadsheet (CSV)" : f.includes("-cues-") ? "Cue sheet (PDF)" : "Prompt book (PDF)");

/** Wait up to `ms` for this device's queue to empty; false if it didn't. */
async function flush(ms: number): Promise<boolean> {
  kick();
  const end = Date.now() + ms;
  while (Date.now() < end) {
    if (!pending.value.some((e) => !e.held)) return true;
    const { promise, resolve } = Promise.withResolvers<void>();
    setTimeout(resolve, 250);
    await promise;
  }
  return !pending.value.some((e) => !e.held);
}

export function ExportDialog() {
  const index = ix.value;
  const [depts, setDepts] = useState<string[] | null>(null);
  const [layers, setLayers] = useState<string[] | null>(null);
  const [promptBook, setPromptBook] = useState(true);
  const [cueSheets, setCueSheets] = useState(true);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState<Job | null>(null);
  const [unsynced, setUnsynced] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const base = `/api/shows/${show.value.showId}/exports`;

  useEffect(() => {
    if (!job || job.state === "done" || job.state === "failed") return;
    const t = setTimeout(() => void api<Job>(`${base}/${job.id}`).then(setJob, (e: Error) => setError(e.message)), 1000);
    return () => clearTimeout(t);
  }, [job]);

  if (!index) return null;
  const chosenDepts = depts ?? index.sortedDepartments.map((d) => d.code);
  const chosenLayers = layers ?? index.sortedLayers.map((l) => l.name);
  const set = (list: string[], x: string, on: boolean) => (on ? [...new Set([...list, x])] : list.filter((y) => y !== x));
  const count = (promptBook ? 1 : 0) + (cueSheets ? 2 : 0);
  const ready = chosenDepts.length > 0 && count > 0;

  const create = async () => {
    const r = await post<Job>(base, { options: { departments: chosenDepts, layers: chosenLayers, promptBook, cueSheets }, revision: show.value.revision });
    setJob(r);
    setUnsynced(0);
    notify(`Export started from saved version ${r.revision}.`);
  };

  const run = async (anyway = false) => {
    setBusy(true);
    setError(null);
    setJob(null);
    try {
      if (!anyway) {
        const synced = await flush(8000);
        await resync();
        const left = pending.value.filter((e) => !e.held).length + proposals.value.length;
        if (!synced || left) {
          setUnsynced(left || pending.value.length);
          return;
        }
      }
      await create();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const footer = (
    <>
      {error && <div class="error" role="alert">{error}</div>}
      {unsynced > 0 ? (
        <div class="stack" data-testid="export-unsynced">
          <div class="banner-inline warn">
            {unsynced} change{unsynced === 1 ? "" : "s"} on this device {unsynced === 1 ? "isn't" : "aren't"} saved (offline, or waiting for your review). Exporting now uses saved version {show.value.revision}, without {unsynced === 1 ? "it" : "them"}. Other
            people's unsent changes are never included.
          </div>
          <button type="button" class="primary wide" disabled={busy} onClick={() => void run(true)}>
            Export saved version {show.value.revision} without my unsent changes
          </button>
          <button type="button" disabled={busy} onClick={() => setUnsynced(0)}>
            Cancel
          </button>
        </div>
      ) : (
        <button type="button" class="primary wide" disabled={busy || !ready || (job !== null && (job.state === "queued" || job.state === "running"))} onClick={() => void run()}>
          {busy ? "Saving changes…" : ready ? `Export ${count} file${count === 1 ? "" : "s"}` : chosenDepts.length ? "Choose something to make" : "Choose at least one department"}
        </button>
      )}
    </>
  );

  return (
    <Sheet title="Export" back="menu" busy={busy} footer={footer}>
      {job && (
        <Group
          title={`From saved version ${job.revision}`}
          footer={
            job.state === "failed"
              ? job.error ?? "The export failed."
              : job.state !== "done"
                ? "Rendering on the server…"
                : job.warnings.length
                  ? job.warnings.map((w) => `⚠ ${w}`).join(" ")
                  : `Made in ${((job.metrics?.wallMs ?? 0) / 1000).toFixed(1)} s. Downloads are private to this show's members.`
          }
        >
          <div data-testid="export-job" data-state={job.state} />
          {job.files.map((f) => (
            <a class="row nav" href={`${base}/${job.id}/files/${encodeURIComponent(f.name)}`} target="_blank" rel="noopener">
              <span class="row-label">{describe(f.name)}</span>
              <span class="row-detail">{f.name}</span>
              <span class="chevron" aria-hidden="true">
                ›
              </span>
            </a>
          ))}
        </Group>
      )}
      <Group title="Make">
        <SwitchRow label="Prompt book" detail="PDF" checked={promptBook} onChange={setPromptBook} />
        <SwitchRow label="Cue sheets" detail="PDF and CSV" checked={cueSheets} onChange={setCueSheets} />
      </Group>
      <Group title="Departments" footer={chosenDepts.length === index.sortedDepartments.length ? "All departments: the full book." : `Only ${chosenDepts.join(", ")}: a filtered book and list.`}>
        {index.sortedDepartments.map((d) => (
          <SwitchRow label={d.code} detail={d.name !== d.code ? d.name : undefined} color={d.color} checked={chosenDepts.includes(d.code)} onChange={(on) => setDepts(set(chosenDepts, d.code, on))} />
        ))}
      </Group>
      {index.sortedLayers.length > 0 && (
        <Group title="Note layers in the prompt book">
          {index.sortedLayers.map((l) => (
            <SwitchRow label={l.name} color={l.color} checked={chosenLayers.includes(l.name)} onChange={(on) => setLayers(set(chosenLayers, l.name, on))} />
          ))}
        </Group>
      )}
    </Sheet>
  );
}
